// ===========================================
// SEARCH MODAL FIELD - ZERO HARDCODE
// Abre SqlexecTable en modo selección
// Reutiliza búsqueda existente
// ===========================================
import { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import SqlexecTable from '../sqlexec/SqlexecTable';
import { useSisConfig } from '../../contexts/SisConfigUi';
import { createPortal } from 'react-dom';
import { secureFetch } from '../../utils/authInterceptor';
import { compensarEscalaVh } from '../../utils/formHelpers';

const SearchModalField = ({
  field,
  value,
  onChange,
  onMultiFieldChange, // 🆕 NUEVO: callback para múltiples campos
  disabled = false,
  error = null,
  touched = false,
  inputStyles = {},
  labelStyles = {},
  errorStyles = {},
  renderMode = 'full',
  externalControl = false,
  isOpen = false,
  onOpenChange = null,
  formData = {}
}) => {
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isModalOpen = externalControl ? isOpen : internalModalOpen;
  const setIsModalOpen = externalControl 
    ? (newState) => onOpenChange?.(newState)
    : setInternalModalOpen;
  const [displayValue, setDisplayValue] = useState('');
  const [modalReady, setModalReady] = useState(false);
  const { modales_ui } = useSisConfig();

  const {
    label,
    placeholder,
    required,
    searchConfig = {}
  } = field;

  const {
    componentForm,
    valueField = 'id',
    displayFields = [],
    displayFormat = '{value}',
    returnFields = [], // 🆕 NUEVO: campos adicionales a retornar
    fixedFilters: fixedFiltersConfig = []
  } = searchConfig;

  // Resuelve fixedFilters (searchConfig) contra formData en vivo — mismo patron
  // que dataSource.params en selects (loadSelectOptions), pero produciendo el
  // shape que ya consume el motor de filtros dinamicos existente (sqlexec.js).
  const resolvedFixedFilters = fixedFiltersConfig.map(fc => {
    if (fc.tipo === 'select' || fc.tipo === 'text') {
      const valor = formData[fc.sourceField];
      return valor === undefined ? null : { campo: fc.campo, tipo: fc.tipo, valor };
    }
    const out = { campo: fc.campo, tipo: fc.tipo };
    let hasValue = false;
    if (fc.desdeField !== undefined && formData[fc.desdeField] !== undefined) {
      out.desde = Number(formData[fc.desdeField]) + (fc.desdeOffset || 0);
      hasValue = true;
    }
    if (fc.hastaField !== undefined && formData[fc.hastaField] !== undefined) {
      out.hasta = Number(formData[fc.hastaField]) + (fc.hastaOffset || 0);
      hasValue = true;
    }
    return hasValue ? out : null;
  }).filter(Boolean);

  // ==========================================
  // CARGAR DISPLAY VALUE INICIAL
  // ==========================================
  useEffect(() => {
    const loadDisplayValue = async () => {
      
      if (!value) return;
      
      if (field.initialDisplayValue) {
        setDisplayValue(field.initialDisplayValue);
        return;
      }

      try {
        const token = sessionStorage.getItem('mapasac_access_token');

        const response = await secureFetch(`/api/sqlexec/${componentForm}/read/${value}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (data.success && data.data) {
          let display = displayFormat;
          displayFields.forEach(fieldName => {
            const placeholder = `{${fieldName}}`;
            display = display.replace(placeholder, data.data[fieldName] || '');
          });

          setDisplayValue(display);
        } else {
          console.warn('⚠️ [SearchModalField] No se pudo obtener data:', data);
        }
      } catch (err) {
        console.error('Error cargando display value:', err);
      }
    };

    loadDisplayValue();
  }, [value, componentForm, displayFields, displayFormat, field.initialDisplayValue]);

  // 🔥 NUEVO: Marcar modal como listo para focus
  useEffect(() => {
    if (isModalOpen) {
      const timer = setTimeout(() => {
        setModalReady(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setModalReady(false);
    }
  }, [isModalOpen]);

  // ==========================================
  // HANDLERS
  // ==========================================
  const handleSelect = (row) => {
    const selectedValue = row[valueField];
    
    let display = displayFormat;
    displayFields.forEach(fieldName => {
      const placeholder = `{${fieldName}}`;
      display = display.replace(placeholder, row[fieldName] || '');
    });
    
    setDisplayValue(display);

    // El propio valor del campo SIEMPRE se fija por su nombre -- independiente
    // de returnFields, que son datos adicionales, no un reemplazo del binding
    // basico del campo (antes quedaban mutuamente excluyentes y con
    // returnFields presente formData[field.name] nunca se llenaba).
    onChange(selectedValue);

    if (onMultiFieldChange) {
      const fieldsToReturn = {};
      returnFields.forEach(fieldName => {
        fieldsToReturn[fieldName] = row[fieldName];
      });

      // 🆕 AGREGAR: Guardar también el displayValue para evitar fetch innecesario
      fieldsToReturn[`${field.name}_display`] = display;

      onMultiFieldChange(fieldsToReturn);
    }

    setIsModalOpen(false);
  };

  const handleClear = () => {
    setDisplayValue('');
    onChange(null);
  };

  const showError = touched && error;

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <>
      {/* 🔥 Solo renderizar input si NO es modo "modal-only" */}
      {renderMode === 'full' && (
        <div>
          <label
            className="block"
            style={{
              fontSize: labelStyles.fontSize || '0.875rem',
              fontWeight: labelStyles.fontWeight || '500',
              color: labelStyles.textColor || '#374151',
              marginBottom: labelStyles.marginBottom || '0.5rem'
            }}
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>

          <div className="relative flex gap-2">
            <input
              type="text"
              value={displayValue}
              readOnly
              placeholder={placeholder}
              disabled={disabled}
              className={`flex-1 cursor-pointer ${showError ? 'border-red-500' : ''}`}
              onClick={() => !disabled && !displayValue && setIsModalOpen(true)}
              style={{
                height: inputStyles.height || '32px',
                fontSize: inputStyles.fontSize || '0.75rem',
                fontFamily: inputStyles.fontFamily || 'inherit',
                paddingLeft: inputStyles.padding?.x || '0.75rem',
                paddingRight: displayValue ? '2.5rem' : (inputStyles.padding?.x || '0.75rem'),
                paddingTop: inputStyles.padding?.y || '0.5rem',
                paddingBottom: inputStyles.padding?.y || '0.5rem',
                borderRadius: inputStyles.borderRadius || '6px',
                borderWidth: inputStyles.borderWidth || '1px',
                borderColor: showError ? '#ef4444' : isFocused ? (inputStyles.focusBorderColor || '#3b82f6') : (inputStyles.borderColor || '#d1d5db'),
                outline: 'none',
                boxShadow: isFocused ? `0 0 0 ${inputStyles.focusRingWidth || '2px'} ${inputStyles.focusBorderColor || '#3b82f6'}40` : 'none',
                backgroundColor: disabled ? (inputStyles.disabledBackgroundColor || '#f3f4f6') : (inputStyles.backgroundColor || '#ffffff'),
                color: inputStyles.textColor || '#111827',
                opacity: disabled ? (inputStyles.disabledOpacity || '0.5') : '1'
              }}
            />

            {displayValue && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X size={16} className="text-gray-400" />
              </button>
            )}

            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              disabled={disabled}
              className="transition-colors"
              style={{
                height: inputStyles.height || '32px',
                width: inputStyles.height || '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: inputStyles.borderRadius || '6px',
                borderWidth: inputStyles.borderWidth || '1px',
                borderColor: inputStyles.borderColor || '#d1d5db',
                backgroundColor: disabled ? (inputStyles.disabledBackgroundColor || '#f3f4f6') : '#ffffff',
                opacity: disabled ? (inputStyles.disabledOpacity || '0.5') : '1',
                cursor: disabled ? 'not-allowed' : 'pointer'
              }}
            >
              <Search size={16} className={disabled ? 'text-gray-300' : 'text-gray-600'} />
            </button>
          </div>

          {showError && (
            <p
              style={{
                fontSize: errorStyles.fontSize || '0.75rem',
                color: errorStyles.textColor || '#dc2626',
                marginTop: errorStyles.marginTop || '0.25rem'
              }}
            >
              {error}
            </p>
          )}
        </div>
      )}

      {/* 🔥 Modal siempre se renderiza (si está abierto) */}
      {isModalOpen && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center z-[60] p-4"
          onClick={() => setIsModalOpen(false)}
          style={{
            backgroundColor: modales_ui?.overlay?.backgroundColor || 'rgba(0, 0, 0, 0.1)',
            backdropFilter: modales_ui?.overlay?.backdropBlur ? `blur(${modales_ui?.overlay?.backdropBlur})` : 'blur(4px)'
          }}
        >
          <div
            className="bg-white w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: compensarEscalaVh(modales_ui?.container?.maxWidth || '95vw'),
              maxHeight: compensarEscalaVh(modales_ui?.container?.maxHeight || '95vh'),
              borderRadius: modales_ui?.container?.borderRadius || '12px',
              boxShadow: modales_ui?.container?.boxShadow || '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            <div 
              className="flex items-center justify-between border-b"
              style={{
                paddingLeft: modales_ui?.header?.padding?.x || '0.75rem',
                paddingRight: modales_ui?.header?.padding?.x || '0.75rem',
                paddingTop: modales_ui?.header?.padding?.y || '0.75rem',
                paddingBottom: modales_ui?.header?.padding?.y || '0.75rem',
                borderBottom: modales_ui?.header?.borderBottom || '1px solid #e5e7eb',
                backgroundColor: modales_ui?.header?.backgroundColor || '#f9fafb'
              }}
            >
              <div className="flex items-center" style={{ gap: '0.75rem' }}>
                <div 
                  className="bg-blue-100 flex items-center justify-center"
                  style={{
                    padding: '0.5rem',
                    borderRadius: '8px'
                  }}
                >
                  <Search className="text-blue-600" size={20} />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">{label}</h2>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>

            <div 
              style={{ 
                display: modales_ui?.content?.display || 'flex',
                flexDirection: modales_ui?.content?.flexDirection || 'column',
                height: compensarEscalaVh(modales_ui?.content?.maxHeight || 'calc(95vh - 140px)'),
                overflow: 'hidden',
                paddingLeft: modales_ui?.content?.paddingX || '0.75rem',
                paddingRight: modales_ui?.content?.paddingX || '0.75rem'
              }}
            >
              <SqlexecTable
                componentForm={componentForm}
                selectionMode={true}
                onSelect={handleSelect}
                onCancel={() => setIsModalOpen(false)}
                autoFocusSearch={modalReady}
                fixedFilters={resolvedFixedFilters}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default SearchModalField;